// Entry point: sign-in state, routes, install prompt, offline cache.
import { Component, render } from 'preact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import './styles.css';
import { cleanAuthParams, configured, finishGoogleSignIn, getSession, isGoogleReturn, onAuthChange, rpc, signInErrorFromUrl, signOut } from './api.js';
import { RouterProvider, match, navigate, useRoute } from './router.js';
import { AppContext } from './state.js';
import { Loading, ToastProvider } from './ui/components.jsx';
import { Brand } from './ui/shell.jsx';
import { Batch, ClassPage, Home, NotFound, Problem, Profile, SignIn } from './pages/student.jsx';
import { AdminBatch, AdminBatches, AdminHome, AdminPeople, AdminPerson, AdminSettings } from './pages/admin.jsx';
import { Timetable } from './pages/timetable.jsx';
import { Updates } from './pages/updates.jsx';
import { DoubtThread, Doubts } from './pages/doubts.jsx';
import { AdminPayments, Fees, Receipt } from './pages/fees.jsx';
import { Attempt, TestPage, Tests, forgetLocalAttempts } from './pages/tests.jsx';
import { TestResults } from './pages/tests-staff.jsx';
import { QuestionBank, QuestionEditor, UploadQuestions } from './pages/questions.jsx';
import { AttendanceList, Register } from './pages/attendance.jsx';
import { BatchProgress, MyProgress, StudentProgress } from './pages/progress.jsx';
import { ChildPage, ParentHome, Sharing } from './pages/parents.jsx';
import { PrivacyPage, PublicPrivacy } from './pages/privacy.jsx';
import { disableNotifications, dropLocalSubscription, resyncNotifications } from './push.js';

const ROUTES = [
  ['/', () => <Home />],
  ['/batches/:id', ({ id }) => <Batch id={id} />],
  ['/batches/:id/progress', ({ id }) => <BatchProgress id={id} key={id} />, 'staff'],
  ['/batches/:id/progress/:person', ({ id, person }) => <StudentProgress batchId={id} personId={person} key={person} />, 'staff'],
  ['/progress', () => <MyProgress />],
  ['/attendance', () => <AttendanceList />, 'staff'],
  ['/attendance/:id', ({ id }) => <Register id={id} key={id} />, 'staff'],
  ['/classes/:id', ({ id }) => <ClassPage id={id} />],
  ['/timetable', () => <Timetable />],
  ['/updates', () => <Updates />],
  ['/doubts', () => <Doubts />],
  ['/doubts/:id', ({ id }) => <DoubtThread id={id} />],
  ['/tests', () => <Tests />],
  ['/tests/attempts/:id', ({ id }) => <Attempt id={id} key={id} />],
  ['/tests/:id', ({ id }) => <TestPage id={id} />],
  ['/tests/:id/results', ({ id }) => <TestResults id={id} />, 'staff'],
  ['/questions', () => <QuestionBank />, 'staff'],
  ['/questions/new', () => <QuestionEditor key="new" />, 'staff'],
  ['/questions/upload', () => <UploadQuestions />, 'staff'],
  ['/questions/:id', ({ id }) => <QuestionEditor id={id} key={id} />, 'staff'],
  ['/fees', () => <Fees />],
  ['/receipts/:id', ({ id }) => <Receipt id={id} />],
  ['/sharing', () => <Sharing />, 'student'],
  ['/privacy', () => <PrivacyPage />],
  ['/profile', () => <Profile />],
  ['/admin', () => <AdminHome />, 'admin'],
  ['/admin/batches', () => <AdminBatches />, 'admin'],
  ['/admin/batches/:id', ({ id }) => <AdminBatch id={id} />, 'admin'],
  ['/admin/people', () => <AdminPeople />, 'admin'],
  ['/admin/people/:id', ({ id }) => <AdminPerson id={id} key={id} />, 'admin'],
  ['/admin/settings', () => <AdminSettings />, 'admin'],
  ['/admin/payments', () => <AdminPayments />, 'admin'],
];

// Parents get their own few pages (their students, receipts, profile).
const PARENT_ROUTES = [
  ['/', () => <ParentHome />],
  ['/children/:id', ({ id }) => <ChildPage id={id} key={id} />],
  ['/receipts/:id', ({ id }) => <Receipt id={id} />],
  ['/privacy', () => <PrivacyPage />],
  ['/profile', () => <Profile />],
];

function Routes({ role }) {
  const { path } = useRoute();
  for (const [pattern, view, needs] of role === 'parent' ? PARENT_ROUTES : ROUTES) {
    const params = match(pattern, path);
    if (!params) continue;
    if (needs === 'admin' && role !== 'admin') return <NotFound />;
    if (needs === 'staff' && !['teacher', 'admin'].includes(role)) return <NotFound />;
    if (needs === 'student' && role !== 'student') return <NotFound />;
    return view(params);
  }
  return <NotFound />;
}

// If a screen ever crashes, show a way out instead of a blank page.
class Boundary extends Component {
  constructor() {
    super();
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    console.error('Screen error:', error);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div class="signin"><main class="signin-card" id="content"><Brand />
        <h1>Something went wrong</h1>
        <p class="signin-lede">Reload the app to carry on. If it keeps happening, tell the academy what you tapped just before.</p>
        <div class="row"><button class="btn btn-navy" type="button" onClick={() => location.reload()}><span>Reload the app</span></button></div>
      </main></div>
    );
  }
}

function Splash() {
  return (
    <div class="splash" role="status" aria-label="Opening the app">
      <Brand />
      <Loading rows={2} />
    </div>
  );
}

function NotConfigured() {
  return (
    <div class="signin"><main class="signin-card" id="content"><Brand />
      <h1>Almost ready</h1>
      <p class="signin-lede">This copy of the app isn&rsquo;t connected to its database yet. Add the Supabase address and public key in the hosting settings, then rebuild.</p>
    </main></div>
  );
}

function App() {
  const { path } = useRoute();
  const [phase, setPhase] = useState('loading'); // loading | signed_out | ready | problem
  const [home, setHome] = useState(null);
  const [problem, setProblem] = useState(null);
  const [signInError, setSignInError] = useState(() => (isGoogleReturn() ? null : signInErrorFromUrl()));
  const [installPrompt, setInstallPrompt] = useState(null);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const synced = useRef(false);
  const loadHome = useCallback(async (quiet) => {
    try {
      const data = await rpc('my_home');
      setHome(data);
      setProblem(null);
      setPhase('ready');
      if (!synced.current) { synced.current = true; resyncNotifications(); }
    } catch (e) {
      if (e.reason === 'signed_out') { await signOut(); setPhase('signed_out'); return; }
      if (quiet && phaseRef.current === 'ready' && !['not_registered', 'blocked', 'wrong_sign_in'].includes(e.reason)) return;
      setProblem(e);
      setPhase('problem');
    }
  }, []);

  useEffect(() => {
    if (!configured) return undefined;
    const off = onAuthChange((event) => {
      if (event === 'SIGNED_OUT') {
        synced.current = false;
        dropLocalSubscription();
        forgetLocalAttempts();
        setHome(null);
        setPhase('signed_out');
      }
    });
    (async () => {
      if (isGoogleReturn()) {
        // A parent coming back from Google: finish the sign-in, then go where they started.
        const { next, error } = await finishGoogleSignIn();
        if (error) setSignInError(error);
        navigate(next, { replace: true, force: true });
      }
      const session = await getSession();
      cleanAuthParams();
      if (!session) { setPhase('signed_out'); return; }
      await loadHome();
    })();
    const onPrompt = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => { off(); window.removeEventListener('beforeinstallprompt', onPrompt); };
  }, []);

  const doSignOut = useCallback(async () => {
    await disableNotifications().catch(() => {}); // this device stops getting this person's notifications
    forgetLocalAttempts(); // and keeps none of their test answers
    await signOut();
    setHome(null);
    setPhase('signed_out');
  }, []);
  const ctx = useMemo(() => ({
    me: home && home.me,
    home,
    reloadHome: loadHome,
    doSignOut,
    installPrompt,
    clearInstallPrompt: () => setInstallPrompt(null),
  }), [home, installPrompt]);

  if (!configured) return <NotConfigured />;
  if (phase === 'loading') return <Splash />;
  if (phase === 'signed_out') return path === '/privacy' ? <PublicPrivacy /> : <SignIn error={signInError} onSignedIn={() => { setPhase('loading'); loadHome(); }} />;
  if (phase === 'problem') return <Problem error={problem} onSignOut={doSignOut} onRetry={() => { setPhase('loading'); loadHome(); }} />;
  return (
    <AppContext.Provider value={ctx}>
      <Routes role={home.me.role} />
    </AppContext.Provider>
  );
}

// The page arrives with a short description of the app for readers without JavaScript (public/index.html);
// the app takes its place.
const root = document.getElementById('app');
root.textContent = '';
render(
  <Boundary>
    <ToastProvider>
      <RouterProvider>
        <App />
      </RouterProvider>
    </ToastProvider>
  </Boundary>,
  root,
);

if ('serviceWorker' in navigator && window.isSecureContext) {
  window.addEventListener('load', () => { navigator.serviceWorker.register('/sw.js').catch(() => {}); });
}
